import React, { useLayoutEffect, useRef, useState } from 'react'

import {
  computeResumePreviewFitBoxHeightPx,
  computeResumePreviewFitBoxWidthPx,
  computeResumePreviewFitScale,
} from '../../utils/resumeDetailSplitLayout'
import { ResumePreviewFitScaleContext } from './resumePreviewFitScaleContext'

type ResumePreviewPanelFitProps = {
  pageWidthPx: number
  children: React.ReactNode
}

/**
 * Fits the fixed-width A4 preview inside the panel; symmetric gutters via margin auto.
 */
export default function ResumePreviewPanelFit({ pageWidthPx, children }: ResumePreviewPanelFitProps) {
  const hostRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(1)
  const [scaledHeight, setScaledHeight] = useState<number | undefined>(undefined)

  useLayoutEffect(() => {
    const host = hostRef.current
    const content = contentRef.current
    if (!host || !content) return

    const update = () => {
      const available = host.clientWidth
      const nextScale = computeResumePreviewFitScale(available, pageWidthPx)
      setScale(nextScale)
      setScaledHeight(computeResumePreviewFitBoxHeightPx(content.offsetHeight, nextScale))
    }

    update()
    const ro = new ResizeObserver(update)
    ro.observe(host)
    ro.observe(content)
    return () => ro.disconnect()
  }, [pageWidthPx, children])

  const scaledWidth = computeResumePreviewFitBoxWidthPx(pageWidthPx, scale)

  return (
    <ResumePreviewFitScaleContext.Provider value={scale}>
      <div ref={hostRef} className="resume-preview-panel-fit w-full min-w-0">
        <div
          className="resume-preview-panel-fit-box mx-auto"
          style={{ width: scaledWidth, height: scaledHeight, overflow: 'hidden' }}
        >
          <div
            ref={contentRef}
            className="resume-preview-panel-fit-content"
            style={{
              width: pageWidthPx,
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
            }}
          >
            {children}
          </div>
        </div>
      </div>
    </ResumePreviewFitScaleContext.Provider>
  )
}
